import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebaseConfig';
import './Login.css';
import './Modal.css';
import './ContactModal.css';

const Login = ({ setEmail }) => {
  const [email, setEmailState] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const navigate = useNavigate();

  // Kurumsal kullanıcı kontrolü fonksiyonu
  const checkIfCorporateUser = async (userEmail) => {
    try {
      const corporateUserRef = doc(database, 'corporateUsers', userEmail);
      const corporateUserDoc = await getDoc(corporateUserRef);
      return corporateUserDoc.exists();
    } catch (error) {
      console.error('Kurumsal kullanıcı kontrolü hatası:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setEmail(user.email);
        
        // Kurumsal kullanıcı kontrolü
        const isCorporate = await checkIfCorporateUser(user.email);
        
        if (isCorporate) {
          // Kurumsal kullanıcıyı kurumsal panele yönlendir
          navigate('/kurumsal/puantajlar');
        } else if (user.email === 'erengursoy90@gmail.com') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, setEmail]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Varsayılan olarak session persistence kullan
      await setPersistence(auth, browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setEmail(userCredential.user.email);
      
      // Kurumsal kullanıcı kontrolü
      const isCorporate = await checkIfCorporateUser(userCredential.user.email);
      
      if (isCorporate) {
        // Kurumsal kullanıcıyı kurumsal panele yönlendir ve uyar
        setMessage('Bu hesap bir kurumsal müşteri hesabıdır. Kurumsal panele yönlendiriliyorsunuz...');
        setTimeout(() => {
          navigate('/kurumsal/puantajlar');
        }, 1500);
      } else if (email === 'erengursoy90@gmail.com') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Giriş hatası:', error);
      setMessage('Giriş başarısız: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="login-section">
        <div className="wave">
          <span></span>
          <span></span>
          <span></span>
        </div>
        
        {/* Logo */}
        <div className="logo-container">
          <h1 className="logo-text" onClick={() => window.location.href = 'https://www.hopssoftware.com'}>Hops Software</h1>
        </div>
        
        <div className="login-container loading-container">
          <div className="loading-spinner-wrapper">
            <div className="loading-spinner"></div>
            <div className="loading-text">Yükleniyor...</div>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="login-footer">
          <div className="footer-content">
            <p>&copy; 2024 Hops Software. Tüm hakları saklıdır.</p>
            <div className="footer-links">
              <a href="#privacy" onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }} className="footer-link">Gizlilik Politikası</a>
              <span className="footer-separator">•</span>
              <a href="#terms" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} className="footer-link">Kullanım Şartları</a>
              <span className="footer-separator">•</span>
              <a href="#support" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} className="footer-link">Destek</a>
              <span className="footer-separator">•</span>
              <a href="/kurumsal" onClick={(e) => { e.preventDefault(); navigate('/kurumsal'); }} className="footer-link">Kurumsal Giriş</a>
            </div>
          </div>
        </footer>

        {/* Privacy Policy Modal */}
        {showPrivacyModal && (
          <div className="legal-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
            <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="legal-modal-header">
                <h2 className="legal-modal-title">Gizlilik Sözleşmesi</h2>
                <button className="legal-modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
              </div>
              <div className="legal-modal-body">
                <section className="legal-modal-section">
                  <h3>1. Veri Toplama ve Kullanım</h3>
                  <p>HOPS olarak, hizmetlerimizi kullanırken sizden toplanan kişisel verilerin gizliliğini korumayı taahhüt ediyoruz. Bu veriler arasında isim, e-posta adresi, telefon numarası ve şirket bilgileri bulunmaktadır.</p>
                </section>
                
                <section className="legal-modal-section">
                  <h3>2. Veri Güvenliği</h3>
                  <p>Verileriniz, endüstri standardı güvenlik protokolleri ve şifreleme teknolojileri kullanılarak korunmaktadır. Yetkisiz erişim, değiştirme veya ifşaya karşı uygun güvenlik önlemleri alınmaktadır.</p>
                </section>
                
                <section className="legal-modal-section">
                  <h3>3. Veri Paylaşımı</h3>
                  <p>Kişisel verileriniz, yasal zorunluluklar dışında üçüncü taraflarla paylaşılmamaktadır. Hizmet sağlayıcılarımızla yapılan veri paylaşımları, gizlilik sözleşmeleri kapsamında gerçekleştirilmektedir.</p>
                </section>
                
                <section className="legal-modal-section">
                  <h3>4. Çerezler ve İzleme</h3>
                  <p>Platformumuzda, kullanıcı deneyimini iyileştirmek için çerezler kullanılmaktadır. Bu çerezler, oturum bilgilerini ve kullanıcı tercihlerini saklamak için kullanılır.</p>
                </section>
                
                <section className="legal-modal-section">
                  <h3>5. Kullanıcı Hakları</h3>
                  <p>KVKK kapsamında, kişisel verilerinize erişim, düzeltme, silme ve işleme itiraz etme haklarına sahipsiniz. Bu haklarınızı kullanmak için bizimle iletişime geçebilirsiniz.</p>
                </section>
                
                <div className="legal-modal-contact">
                  <h3>İletişim Bilgileri</h3>
                  <p>Gizlilik politikamız hakkında sorularınız için:</p>
                  <p>E-posta: info@hopssoftware.com</p>
                  <p>Telefon: +90 552 464 11 16</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Terms Modal */}
        {showTermsModal && (
          <div className="legal-modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="legal-modal-header">
                <h2 className="legal-modal-title">Mesafeli Satış Sözleşmesi</h2>
                <button className="legal-modal-close" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="legal-modal-body">
                <div className="legal-modal-section">
                  <h3>1. Taraflar</h3>
                  <p>İşbu sözleşme, HOPS (bundan sonra SATICI olarak anılacaktır) ile alıcı (bundan sonra ALICI olarak anılacaktır) arasında aşağıdaki şartlar çerçevesinde düzenlenmiştir.</p>
                </div>
                <div className="legal-modal-section">
                  <h3>2. Sözleşmenin Konusu</h3>
                  <p>İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait platform üzerinden satın aldığı kontör hizmetinin satışı ve teslimi ile ilgili olarak 6502 Sayılı Tüketicinin Korunması Hakkında Kanun hükümleri gereğince tarafların hak ve yükümlülüklerinin belirlenmesidir.</p>
                </div>
                <div className="legal-modal-section">
                  <h3>3. Hizmet Bilgileri</h3>
                  <p>Satın alınan kontör paketi, dijital bir hizmet olup, ödemenin tamamlanmasını takiben ALICI'nın hesabına otomatik olarak tanımlanacaktır. Kontörler, aktivasyon tarihinden itibaren süresiz olarak geçerlidir.</p>
                </div>
                <div className="legal-modal-section">
                  <h3>4. Ödeme ve Teslimat</h3>
                  <p>Ödeme, platform üzerinden kredi kartı veya banka kartı ile gerçekleştirilir. Kontörler, ödemenin başarıyla tamamlanmasının ardından anında hesaba tanımlanır. Dijital hizmet olması sebebiyle fiziksel teslimat söz konusu değildir.</p>
                </div>
                <div className="legal-modal-section">
                  <h3>5. Cayma Hakkı</h3>
                  <p>Dijital içerik/hizmet sunumu niteliğinde olan kontör satışlarında, hizmetin tesliminin/ifasının başladığı an itibarıyla cayma hakkı kullanılamaz. Bu husus 6502 Sayılı Kanun'un 15/1-ğ maddesi uyarınca cayma hakkı istisnası kapsamındadır.</p>
                </div>
                <div className="legal-modal-contact">
                  <h3>Satıcı Bilgileri</h3>
                  <p>Unvan: TUĞBA HAZAL ÖZKARAASLAN HOPS YAZILIM SİSTEMLERİ TEKNOPARK ŞUBESİ</p>
                  <p>Adres: Yeniköy Merkez Mah. Vatan Cad. Teknopark Sitesi Başiskele / Kocaeli</p>
                  <p>E-posta: info@hopssoftware.com</p>
                  <p>Telefon: +90 552 464 11 16</p>
                  <p>MERSİS No: 3888146357 / 154133525540002</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact/Support Modal */}
        {showContactModal && (
          <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
            <div className="contact-modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="contact-modal-header">
                <div className="contact-modal-header-content">
                  <h2 className="contact-modal-title">BİZİ ARAYIN</h2>
                  <p className="contact-modal-subtitle">Sizi arayalım ve ihtiyacınız olan çözümü sunalım</p>
                </div>
                <div className="contact-modal-header-right">
                  <span className="contact-modal-phone">+90 552 464 11 16</span>
                  <button className="contact-modal-close" onClick={() => setShowContactModal(false)}>×</button>
                </div>
              </div>
              <div className="contact-modal-body">
                <form className="contact-modal-form">
                  <div className="contact-form-group">
                    <input type="text" placeholder="Adınız Soyadınız" className="contact-form-input" required />
                  </div>
                  <div className="contact-form-group">
                    <input type="text" placeholder="Firma Adınız" className="contact-form-input" required />
                  </div>
                  <div className="contact-form-group">
                    <div className="contact-phone-group">
                      <select className="contact-country-code">
                        <option value="+90">+90</option>
                      </select>
                      <input type="tel" placeholder="Telefon Numaranız" className="contact-form-input contact-phone-input" required />
                    </div>
                  </div>
                  <div className="contact-form-group">
                    <input type="email" placeholder="E-posta Adresiniz" className="contact-form-input" required />
                  </div>
                  <div className="contact-agreements">
                    <div className="contact-agreement-item">
                      <div className="contact-checkbox-container">
                        <input type="checkbox" className="contact-checkbox" id="contact-privacy" required />
                        <svg className="contact-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      </div>
                      <label htmlFor="contact-privacy" className="contact-agreement-text">
                        Gizlilik sözleşmesini okudum ve kabul ediyorum
                      </label>
                    </div>
                    <div className="contact-agreement-item">
                      <div className="contact-checkbox-container">
                        <input type="checkbox" className="contact-checkbox" id="contact-commercial" />
                        <svg className="contact-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      </div>
                      <label htmlFor="contact-commercial" className="contact-agreement-text">
                        Ticari elektronik ileti almayı kabul ediyorum
                      </label>
                    </div>
                  </div>
                  <button type="submit" className="contact-submit-button">Gönder</button>
                </form>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="login-section">
      <div className="wave">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      {/* Logo */}
      <div className="logo-container">
        <h1 className="logo-text" onClick={() => window.location.href = 'https://www.hopssoftware.com'}>Hops Software</h1>
      </div>
      
      <div className="login-container">
        <h2>Giriş Yap</h2>
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="email"
            placeholder="E-posta"
            value={email}
            onChange={(e) => setEmailState(e.target.value)}
            required
            className="login-input"
          />
          <input
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="login-input"
          />
          

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <div className="button-loading">
                <div className="button-spinner"></div>
                <span>Giriş Yapılıyor...</span>
              </div>
            ) : (
              'Giriş Yap'
            )}
          </button>
          {message && <p className="login-message">{message}</p>}
        </form>
        
        <div className="forgot-password-link-container">
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate('/forgot-password');
            }} 
            className="forgot-password-link"
          >
            Şifremi Unuttum?
          </button>
        </div>
        
        <div className="separator"></div>
        
        <button type="button" onClick={() => navigate('/signup')} className="signup-button" disabled={loading}>
          Kayıt Ol
        </button>
      </div>
      
      {/* Footer */}
      <footer className="login-footer">
        <div className="footer-content">
          <p>&copy; 2024 Hops Software. Tüm hakları saklıdır.</p>
          <div className="footer-links">
            <a href="#privacy" onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }} className="footer-link">Gizlilik Politikası</a>
            <span className="footer-separator">•</span>
            <a href="#terms" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} className="footer-link">Kullanım Şartları</a>
            <span className="footer-separator">•</span>
            <a href="#support" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} className="footer-link">Destek</a>
            <span className="footer-separator">•</span>
            <a href="/kurumsal" onClick={(e) => { e.preventDefault(); navigate('/kurumsal'); }} className="footer-link">Kurumsal Giriş</a>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="legal-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
            <div className="legal-modal-body">
              <h2 className="legal-modal-title">Gizlilik Sözleşmesi</h2>
              
              <h3>1. Veri Toplama ve Kullanım</h3>
              <p>HOPS olarak, hizmetlerimizi kullanırken sizden toplanan kişisel verilerin gizliliğini korumayı taahhüt ediyoruz. Bu veriler arasında isim, e-posta adresi, telefon numarası ve şirket bilgileri bulunmaktadır.</p>
              
              <h3>2. Veri Güvenliği</h3>
              <p>Verileriniz, endüstri standardı güvenlik protokolleri ve şifreleme teknolojileri kullanılarak korunmaktadır. Yetkisiz erişim, değiştirme veya ifşaya karşı uygun güvenlik önlemleri alınmaktadır.</p>
              
              <h3>3. Veri Paylaşımı</h3>
              <p>Kişisel verileriniz, yasal zorunluluklar dışında üçüncü taraflarla paylaşılmamaktadır. Hizmet sağlayıcılarımızla yapılan veri paylaşımları, gizlilik sözleşmeleri kapsamında gerçekleştirilmektedir.</p>
              
              <h3>4. Çerezler ve İzleme</h3>
              <p>Platformumuzda, kullanıcı deneyimini iyileştirmek için çerezler kullanılmaktadır. Bu çerezler, oturum bilgilerini ve kullanıcı tercihlerini saklamak için kullanılır.</p>
              
              <h3>5. Kullanıcı Hakları</h3>
              <p>KVKK kapsamında, kişisel verilerinize erişim, düzeltme, silme ve işleme itiraz etme haklarına sahipsiniz. Bu haklarınızı kullanmak için bizimle iletişime geçebilirsiniz.</p>
              
              <div className="legal-modal-contact">
                <h3>İletişim Bilgileri</h3>
                <p>Gizlilik politikamız hakkında sorularınız için:</p>
                <p>E-posta: info@hopssoftware.com</p>
                <p>Telefon: +90 552 464 11 16</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="legal-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowTermsModal(false)}>×</button>
            <div className="legal-modal-body">
              <h2 className="legal-modal-title">Mesafeli Satış Sözleşmesi</h2>
              
              <h3>1. Taraflar</h3>
              <p>İşbu sözleşme, HOPS (bundan sonra SATICI olarak anılacaktır) ile alıcı (bundan sonra ALICI olarak anılacaktır) arasında aşağıdaki şartlar çerçevesinde düzenlenmiştir.</p>
              
              <h3>2. Sözleşmenin Konusu</h3>
              <p>İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait platform üzerinden satın aldığı kontör hizmetinin satışı ve teslimi ile ilgili olarak 6502 Sayılı Tüketicinin Korunması Hakkında Kanun hükümleri gereğince tarafların hak ve yükümlülüklerinin belirlenmesidir.</p>
              
              <h3>3. Hizmet Bilgileri</h3>
              <p>Satın alınan kontör paketi, dijital bir hizmet olup, ödemenin tamamlanmasını takiben ALICI'nın hesabına otomatik olarak tanımlanacaktır. Kontörler, aktivasyon tarihinden itibaren süresiz olarak geçerlidir.</p>
              
              <h3>4. Ödeme ve Teslimat</h3>
              <p>Ödeme, platform üzerinden kredi kartı veya banka kartı ile gerçekleştirilir. Kontörler, ödemenin başarıyla tamamlanmasının ardından anında hesaba tanımlanır. Dijital hizmet olması sebebiyle fiziksel teslimat söz konusu değildir.</p>
              
              <h3>5. Cayma Hakkı</h3>
              <p>Dijital içerik/hizmet sunumu niteliğinde olan kontör satışlarında, hizmetin tesliminin/ifasının başladığı an itibarıyla cayma hakkı kullanılamaz. Bu husus 6502 Sayılı Kanun'un 15/1-ğ maddesi uyarınca cayma hakkı istisnası kapsamındadır.</p>
              
              <div className="legal-modal-contact">
                <h3>Satıcı Bilgileri</h3>
                <p>Unvan: TUĞBA HAZAL ÖZKARAASLAN HOPS YAZILIM SİSTEMLERİ TEKNOPARK ŞUBESİ</p>
                <p>Adres: Yeniköy Merkez Mah. Vatan Cad. Teknopark Sitesi Başiskele / Kocaeli</p>
                <p>E-posta: info@hopssoftware.com</p>
                <p>Telefon: +90 552 464 11 16</p>
                <p>MERSİS No: 3888146357 / 154133525540002</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Login;